import { camelCase, startCase } from 'lodash';
import { Chart } from 'primereact/chart';
import { Galleria } from 'primereact/galleria';
import { Panel } from 'primereact/panel';
import { Tag } from 'primereact/tag';
import React, { useContext, useEffect, useState } from 'react';
import axiosWithAuth from '../../../helpers/AxiosHelper';
import errorMessage from '../../../helpers/errorMessage';
import { ThemeContext } from '../../../context/ThemeContext';
import { CHART_COLORS, cyclePalette, BASE_OPTIONS, applyChartJsTheme, applyChartColorsTheme } from '../../reporting/chart_theme';
import { formatCount, formatCurrency, formatPercentage, sortCurrencies, sumByCurrency } from "./platformOverviewColumns";
import '../platforms.css';

// Same icon-badge tile as data.js's SummaryTile (the Exaloan Dashboard's
// aggregate tiles) - not shared from there since that one isn't exported and
// this is a small enough shape to not be worth extracting across two
// unrelated features for, but kept visually identical on purpose so the
// Exaloan and platform-account variants of the Dashboard read as one design.
// PrimeReact's own Galleria theme hardcodes its thumbnail strip to
// `rgba(0, 0, 0, 0.9)` in BOTH exaloan-primereact-theme.css and its dark
// counterpart, identically - a generic photo-viewer filmstrip scrim, never
// actually given a light-mode variant for either file. Harmless for a real
// photo gallery, but this component reuses Galleria as a chart-type tab
// strip, so it read as "always dark" regardless of the light/dark toggle -
// not a light/dark inconsistency, both files agree, just never designed for
// this context. Scoped to this component's own class (Galleria isn't used
// anywhere else in the app) so a future real photo Galleria elsewhere keeps
// its intended dark scrim look untouched.
// !important on every property: PrimeReact's own rule this overrides is
// `html:not([data-theme="dark"]) .p-galleria .p-galleria-thumbnail-container`
// (and the [data-theme="dark"] equivalent) - two classes plus a type
// selector plus a :not() pseudo-class, higher specificity than any plain
// two-class selector here could match without an equally ugly selector of
// its own. Confirmed necessary: adding the className alone (previously
// missing from the Galleria elements below) still lost to PrimeReact's
// rule on both themes.
const OVERVIEW_GALLERIA_CSS = `
.overview-chart-galleria .p-galleria-thumbnail-container {
    background: var(--surface-card) !important;
    border-top: 1px solid var(--surface-border);
}
.overview-chart-galleria .p-galleria-thumbnail-container .p-galleria-thumbnail-prev,
.overview-chart-galleria .p-galleria-thumbnail-container .p-galleria-thumbnail-next {
    color: var(--text-color) !important;
}
`;

// Now ui-kit.css's .ex-stat tile - the same markup data.js's SummaryTile uses.
const StatTile = ({ icon, label, value }) => (
    <div className="ex-stat">
        <span className="ex-stat-icon"><i className={`pi ${icon}`} /></span>
        {/* .ex-stat-body has min-width:0 - without it a long label/value can
            force this flex child wider than its column, overflowing all the
            way out to a page-wide scrollbar. */}
        <div className="ex-stat-body">
            <div className="ex-stat-label">{label}</div>
            <div className="ex-stat-value">{value}</div>
        </div>
    </div>
);

// The actual "one platform's detail" content (header stats + SME/CONSUMER
// breakdown tables and charts) - extracted out of PlatformOverviewDetails so
// it can be shared by two callers: that component (the standalone
// /dashboard/:platformName page Exaloan staff click through to from the
// summary table) and data.js's merged Dashboard page, which renders this
// inline below the summary table for an external platform account instead -
// that account only ever has the one row anyway, so a second page/click for
// it would just be an extra step to see the only platform it's allowed to.
// A platform's overview covers all its loans: `by_currency` holds the KPIs
// per loan-tape currency. Documents in the older SME/CONSUMER shape (still
// written by the previous prod Lambda until its cutover) are read as before.
const isPlatformLevel = (doc) => doc?.by_currency != null;

// Each currency of an older document's data type as { currency, figures }.
// Those written before by_currency existed are in the document's
// original_currency.
const segmentCurrencies = (doc, segment) => {
    const seg = doc?.[segment];
    if (!seg) return [];
    if (!seg.by_currency) return [{ currency: doc.original_currency, figures: seg }];
    return sortCurrencies(Object.keys(seg.by_currency)).map((currency) => ({ currency, figures: seg.by_currency[currency] }));
}

// Origination volume since inception per currency - the document-level total
// only exists when the whole document has one currency, otherwise each
// currency's own volume (older documents: SME and CONSUMER summed within each
// currency).
const originationVolumeByCurrency = (doc) => {
    if (doc?.total_origination_volume != null) return [{ currency: doc.original_currency, value: doc.total_origination_volume }];
    if (isPlatformLevel(doc)) {
        return sortCurrencies(Object.keys(doc.by_currency))
            .filter((currency) => doc.by_currency[currency].origination_volume != null)
            .map((currency) => ({ currency, value: doc.by_currency[currency].origination_volume }));
    }
    return sumByCurrency(['SME', 'CONSUMER'].map((segment) => segmentCurrencies(doc, segment)
        .filter(({ figures }) => figures.origination_volume != null)
        .map(({ currency, figures }) => ({ currency, value: figures.origination_volume }))));
}

// [{ key, header, platformCalculations }] - the chart panels: one per
// currency of the platform, or, for an older document, one per data type
// (and currency, when that data type has several).
const chartSections = (doc) => {
    if (isPlatformLevel(doc)) {
        const currencies = sortCurrencies(Object.keys(doc.by_currency));
        return currencies.map((currency) => ({
            key: currency,
            header: currencies.length > 1 ? `Loan Overview (${currency})` : 'Loan Overview',
            platformCalculations: doc.by_currency[currency].platform_calculations,
        }));
    }
    const sections = [];
    for (const segment of ['SME', 'CONSUMER']) {
        const currencies = segmentCurrencies(doc, segment);
        if (currencies.length > 1) {
            for (const { currency, figures } of currencies) {
                sections.push({ key: `${segment}-${currency}`, header: `${segment} Overview (${currency})`, platformCalculations: figures.platform_calculations });
            }
        } else if (doc?.[segment]) {
            sections.push({ key: segment, header: `${segment} Overview`, platformCalculations: doc[segment].platform_calculations });
        }
    }
    return sections;
}

export const PlatformOverviewDetailBody = ({ platformName, setLoading = () => {} }) => {
    const [currentPlatformDetails, setCurrentPlatformDetails] = useState(null)
    // [{ key, header, charts }] - see chartSections.
    const [chartPanels, setChartPanels] = useState([])
    const { theme } = useContext(ThemeContext);

    // This page never called either of these, so its pie charts always
    // rendered with Chart.js's own unthemed default dataset colors (no
    // backgroundColor was ever set below, see extractChartData/the Chart
    // data prop) instead of the app's actual light/dark palette - looked
    // "always dark" regardless of the theme toggle since nothing here ever
    // reacted to it. Same wiring PlatformReport.js already uses.
    useEffect(() => {
        applyChartJsTheme(theme);
        applyChartColorsTheme(theme);
    }, [theme]);

    useEffect(() => {
        if (!platformName) return;
        setLoading(true)
        axiosWithAuth.get(`/overview/${platformName}`).then((response) => {
            let data = response.data;
            const doc = data[0];
            if (isPlatformLevel(doc) || (doc?.hasOwnProperty('SME') && doc?.hasOwnProperty('CONSUMER'))) {
                // Platform-reported figures only - exaloan_calculations (our
                // own recalculated status/EAD-based figures) deliberately
                // left out, so a platform only ever sees what it itself
                // reported, not our internal re-derivation of it.
                const panels = chartSections(doc).map(({ key, header, platformCalculations }) => ({
                    key, header, charts: extractChartData(platformCalculations),
                }));
                setCurrentPlatformDetails(data);
                setChartPanels(panels.filter((panel) => panel.charts.length !== 0));
            }
            setLoading(false);
        }).catch((err) => {
            console.error('Overview detail fetch failed:', errorMessage(err));
            setLoading(false)
        })
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [platformName]);

    const extractChartData = (data) => {
        let chartData = [];
        for (let chart in data) {
            if (chart !== 'recovery_rate') {
                let dataset = {
                    label: startCase(camelCase(chart)),
                    data: Object.values(data[chart]),
                    labels: Object.keys(data[chart])
                };
                chartData.push(dataset);
            }
        }
        return chartData;
    }

    // BASE_OPTIONS carries the theme-aware font already; ChartJS.defaults
    // (set by applyChartJsTheme above) covers legend/tick/grid text and
    // border colors globally, so nothing chart-specific is needed here
    // beyond the dataset's own slice colors below.
    const chartOptions = BASE_OPTIONS;

    if (!currentPlatformDetails) return null;
    const platformDetail = currentPlatformDetails[0];
    const originationVolumes = originationVolumeByCurrency(platformDetail);
    const panelClassName = chartPanels.length > 1 ? "lg:col-6 md:col-12 sm:col-12" : "lg:col-12 md:col-12 sm:col-12";

    return (
        <div className="grid nested-grid overview-detail">
            <style>{OVERVIEW_GALLERIA_CSS}</style>
            <div className="col-12">
                <section className="ex-section">
                    <div className="ex-section-header">
                        <h3 className="ex-section-title">Your Data With Us</h3>
                    </div>
                    <div className="ex-stat-grid">
                        <StatTile icon="pi-list" label="Total Loan Count" value={formatCount(platformDetail?.total_loan_count)} />
                        <StatTile icon="pi-globe" label="Original Currency" value={platformDetail?.original_currency ?? '-'} />
                        {isPlatformLevel(platformDetail) && (
                            <StatTile icon="pi-exclamation-triangle" label="Default Rate" value={formatPercentage(platformDetail.default_rate_by_platform_status)} />
                        )}
                        {originationVolumes.length > 1
                            ? originationVolumes.map(({ currency, value }) => (
                                <StatTile key={currency} icon="pi-wallet" label={`Origination Volume Since Inception (${currency})`} value={formatCurrency(value, currency)} />
                            ))
                            : <StatTile icon="pi-wallet" label="Origination Volume Since Inception" value={formatCurrency(originationVolumes[0]?.value, originationVolumes[0]?.currency)} />}
                    </div>
                </section>
            </div>

            <div className="col-12">
                <div className="grid">
                    {chartPanels.map((panel) => (<div key={panel.key} className={panelClassName}>
                        <Panel header={panel.header} toggleable className="ex-overview-panel">
                            <div>
                                <Galleria
                                    className="overview-chart-galleria"
                                    value={panel.charts}
                                    thumbnail={(chart) => (
                                        <Tag className="m-auto sm:mx-3" style={{ 'fontSize': '0.75em', 'textAlign': 'center' }}>{chart.label}</Tag>
                                    )}
                                    item={(chart) => (
                                        <div className="chart-container">
                                            <Chart type="pie" data={{ labels: chart.labels, datasets: [{ data: chart.data, backgroundColor: cyclePalette(CHART_COLORS.categorical, chart.data.length) }] }} options={chartOptions} />
                                        </div>
                                    )}
                                />
                            </div>
                        </Panel>
                    </div>))}
                </div>
            </div>
        </div>
    );
};

export default PlatformOverviewDetailBody;
